import React from 'react';

import { IconButton } from '@atlaskit/button/new';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { token } from '@atlaskit/tokens';

import { useSendSideNavCreateButtonClick } from '@townsquare/analytics-nav4';
import { CommonDraggableSideNavItemProps, DefaultItemType } from '@townsquare/griffin-navigation/types';
import { matchRoute } from '@townsquare/router/primitives/match-route';
import { useQueryParams } from '@townsquare/router/use-query-params';
import { useRouter } from '@townsquare/router/useRouter';
import { getScreenFromStringOrDefault, ScreenType } from '@townsquare/staff-directory-view/screen-type';

export type MenuItemProps = CommonDraggableSideNavItemProps & { item: DefaultItemType } & {
	matchingDirectoryScreens: ScreenType[];
};

export const MenuItem = ({
	onClick,
	item,
	matchingDirectoryScreens,
	hasDragIndicator,
	draggableRef,
	isDragging,
	dropIndicator,
	visualContentRef,
}: MenuItemProps): React.JSX.Element => {
	const sendCreateButtonClickEvent = useSendSideNavCreateButtonClick();
	const { hoverAction } = item;
	const [routerStore] = useRouter();

	const queryParams = useQueryParams();
	const isSelected =
		'screen' in queryParams
			? matchingDirectoryScreens.includes(getScreenFromStringOrDefault(queryParams['screen']))
			: Boolean(matchRoute(item.matches, routerStore.location.pathname, undefined));

	return (
		<LinkMenuItem
			key={item.analyticsSubject}
			onClick={e => onClick(item, e)}
			isSelected={isSelected}
			href={item.path}
			elemBefore={<item.Icon label="" />}
			testId={item.testId}
			hasDragIndicator={hasDragIndicator}
			ref={draggableRef}
			isDragging={isDragging}
			dropIndicator={dropIndicator}
			visualContentRef={visualContentRef}
			actionsOnHover={
				hoverAction ? (
					<IconButton
						icon={iconProps => (
							<hoverAction.Icon
								{...iconProps}
								size="small"
								color={isSelected ? token('color.icon.selected') : undefined}
							/>
						)}
						appearance="subtle"
						spacing="compact"
						label={hoverAction.label}
						tooltip={{ content: hoverAction.label }}
						isTooltipDisabled={false}
						onClick={() => {
							sendCreateButtonClickEvent(item.analyticsSubject);
							hoverAction?.onClick?.();
						}}
					/>
				) : null
			}
		>
			{item.name}
		</LinkMenuItem>
	);
};
